import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Button,
  PageHeader,
  Popconfirm,
  Typography,
  Row,
  Col,
  Input,
  List,
  Collapse,
  Space,
  Card,
  Spin,
  message,
  Form,
} from "antd";
import { Link } from "react-router-dom";
import moment from "moment";
import Page from "_components/Page";
import { referenceService } from "_services";
import { ReferenceDataForm } from "./ReferenceDataForm";

const { Panel } = Collapse;
const { Title, Text } = Typography;
const { Search } = Input;

const References = () => {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchItemText, setSearchItemText] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  useEffect(() => {
    getGroups();
  }, []);

  const getGroups = () => {
    setLoading(true);
    referenceService
      .searchGroups()
      .then((data) => {
        setGroups(data.content);
        setFilteredGroups(data.content);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setError(error);
      });
  };

  const getItems = (typeId) => {
    setLoading(true);
    referenceService
      .getAllReferenceDataByType(typeId)
      .then((data) => {
        setItems(data.content);
        setFilteredItems(data.content);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setError(error);
      });
  };

  const handleTypeClick = (referenceType, group) => {
    setSelectedType(referenceType);
    setSelectedGroup(group);
    getItems(referenceType.id);
  };

  const handleItemClick = (item) => {
    item.startDate = item.startDate && moment(item.startDate);
    item.endDate = item.endDate && moment(item.endDate);
    setSelectedItem(item);
    setShowEditForm(true);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const lower = value.toLowerCase();
    const filtered = groups
      .map((group) => ({
        ...group,
        referenceTypes: group.referenceTypes.filter((type) =>
          type.name.toLowerCase().includes(lower)
        ),
      }))
      .filter(
        (group) =>
          group.name.toLowerCase().includes(lower) ||
          group.referenceTypes.length > 0
      );

    setFilteredGroups(filtered);
  };

  const handleItemSearch = (value) => {
    setSearchItemText(value);
    const lower = value.toLowerCase();
    const filtered = items.filter(
      (item) =>
        item.name.toLowerCase().includes(lower) ||
        item.code.toLowerCase().includes(lower)
    );

    setFilteredItems(filtered);
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setError(null);
        setLoading(true);
        values.category = selectedType && selectedType.category;
        values.startDate =
          values.startDate && moment(values.startDate).format("YYYY-MM-DD");
        values.endDate =
          values.endDate && moment(values.endDate).format("YYYY-MM-DD");
        if (!selectedItem.id) {
          referenceService
            .create(values)
            .then((data) => {
              form.resetFields();
              setSelectedItem(null);
              setShowAddForm(false);
              setLoading(false);
              message.success(
                `Reference data ${data.content.code} successfully created!`
              );
              getItems(selectedType.id);
            })
            .catch((error) => {
              setLoading(false);
              setError(error);
            });
        } else {
          referenceService
            .update(selectedItem.id, values)
            .then((data) => {
              form.resetFields();
              setSelectedItem(null);
              setShowEditForm(false);
              setLoading(false);
              message.success(
                `Reference data ${data.content.code} successfully updated!`
              );
              getItems(selectedType.id);
            })
            .catch((error) => {
              setLoading(false);
              setError(error);
            });
        }
      })
      .catch((error) => {});
  };

  const onAdd = () => {
    setSelectedItem({});
    setShowAddForm(true);
  };

  const onCancel = () => {
    setSelectedItem(null);
    setShowAddForm(false);
    setShowEditForm(false);
  };

  const getActionButtons = () => {
    let buttons = [];
    if (selectedType && !showAddForm) {
      buttons.push(
        <Button key="add" htmlType="button" onClick={onAdd}>
          Add
        </Button>
      );
    }
    if (selectedType && (showAddForm || showEditForm)) {
      buttons.push(
        <Button
          key="save"
          type="primary"
          htmlType="button"
          onClick={handleFormSubmit}
        >
          Save
        </Button>
      );
    }
    if (selectedType) {
      buttons.push(
        <Button key="cancel" htmlType="button" onClick={onCancel}>
          Cancel
        </Button>
      );
    }

    return buttons;
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/reference">Reference Search</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getActionButtons()}
      >
        <Page inner error={error}>
          <Spin tip="Loading..." spinning={loading}>
            <Row gutter={24}>
              <Col span={8}>
                <Search
                  placeholder="Search group or type..."
                  allowClear
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ marginBottom: 16 }}
                />
                <Collapse
                  activeKey={filteredGroups.map((g) => g.id.toString())}
                >
                  {filteredGroups.map((group) => (
                    <Panel header={group.name} key={group.id}>
                      <List
                        dataSource={group.referenceTypes}
                        renderItem={(type) => (
                          <List.Item
                            onClick={() => handleTypeClick(type, group)}
                            style={{ cursor: "pointer" }}
                          >
                            {type.name} ({type.code})
                          </List.Item>
                        )}
                      />
                    </Panel>
                  ))}
                </Collapse>
              </Col>
              <Col span={8}>
                {selectedType && (
                  <>
                    <Search
                      placeholder="Search type..."
                      allowClear
                      value={searchItemText}
                      onChange={(e) => handleItemSearch(e.target.value)}
                      style={{ marginBottom: 16 }}
                    />
                    <Collapse activeKey={selectedType.id}>
                      <Panel header={selectedType.name} key={selectedType.id}>
                        <List
                          dataSource={filteredItems}
                          renderItem={(item) => (
                            <List.Item
                              onClick={() => handleItemClick(item)}
                              style={{ cursor: "pointer" }}
                            >
                              {item.name} ({item.code})
                            </List.Item>
                          )}
                        />
                      </Panel>
                    </Collapse>
                  </>
                )}
              </Col>
              <Col span={8}>
                {(showAddForm || showEditForm) && (
                  <ReferenceDataForm
                    form={form}
                    data={selectedItem}
                    typeId={selectedItem && selectedItem.id}
                  />
                )}
              </Col>
            </Row>
          </Spin>
        </Page>
      </PageHeader>
    </div>
  );
};

export { References };
